import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { 
  Shield, 
  Bell, 
  Palette, 
  Globe, 
  Key, 
  Users, 
  Mail,
  Moon,
  Sun,
  Smartphone,
  Monitor,
  Database,
  Lock,
  Eye,
  Download,
  Trash2,
  Save
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PushNotificationSettings } from "@/components/PushNotificationSettings";
import { useAuth } from "@/contexts/AuthContext";
import UserApprovals from "@/pages/UserApprovals";

const Settings = () => {
  const { toast } = useToast();
  const { profile } = useAuth();
  
  const [settings, setSettings] = useState(() => {
    const savedSettings = localStorage.getItem('userSettings');
    return savedSettings ? JSON.parse(savedSettings) : {
      // Notificações
      emailNotifications: true,
      pushNotifications: false,
      smsNotifications: true,
      caseUpdates: true,
      deadlineAlerts: true,
      clientMessages: true,
      showInactiveAlerts: true, // Nova configuração para alertas de inatividade
      
      // Aparência
      theme: "light",
      fontSize: "medium",
      compactMode: false,
      
      // Segurança
      twoFactorAuth: false,
      sessionTimeout: "1hour",
      loginAlerts: true,
      
      // Privacidade
      dataSharing: false,
      analyticsTracking: true,
      marketingEmails: false,
      
      // Workspace
      autoSave: true,
      defaultView: "list",
      itemsPerPage: "25"
    };
  });

  // Salva as configurações no localStorage sempre que elas mudam
  useEffect(() => {
    localStorage.setItem('userSettings', JSON.stringify(settings));
  }, [settings]);

  const handleSave = () => {
    toast({
      title: "Configurações salvas!",
      description: "Suas preferências foram atualizadas com sucesso.",
    });
  };

  const handleExportData = () => {
    toast({
      title: "Exportação iniciada",
      description: "Seus dados serão enviados por e-mail em alguns minutos.",
    });
  };

  const handleDeleteAccount = () => {
    toast({
      title: "Conta não pode ser excluída",
      description: "Entre em contato com o suporte para excluir sua conta.",
      variant: "destructive"
    });
  };

  return (
    <div className="container mx-auto p-6 space-y-8 max-w-4xl">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-foreground">Configurações</h1>
        <p className="text-muted-foreground">
          Gerencie suas preferências e configurações do sistema
        </p>
      </div>

      <Tabs defaultValue="notifications" className="space-y-6">
        <TabsList className={`grid w-full ${profile?.role === "CEO" ? "grid-cols-6" : "grid-cols-5"}`}>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notificações
          </TabsTrigger>
          <TabsTrigger value="appearance" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Aparência
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Segurança
          </TabsTrigger>
          <TabsTrigger value="privacy" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Privacidade
          </TabsTrigger>
          <TabsTrigger value="data" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Dados
          </TabsTrigger>
          {profile?.role === "CEO" && (
            <TabsTrigger value="admin-users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Usuários
            </TabsTrigger>
          )}
        </TabsList>

        {/* Notificações */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Preferências de Notificação</CardTitle>
              <CardDescription>
                Configure como e quando você deseja receber notificações
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h4 className="font-medium">Canais de Notificação</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <Label htmlFor="email-notifications">Notificações por E-mail</Label>
                    </div>
                    <Switch
                      id="email-notifications"
                      checked={settings.emailNotifications}
                      onCheckedChange={(checked) => 
                        setSettings({...settings, emailNotifications: checked})
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Smartphone className="h-4 w-4 text-muted-foreground" />
                      <Label htmlFor="push-notifications">Notificações Push</Label>
                    </div>
                    <Switch
                      id="push-notifications"
                      checked={settings.pushNotifications}
                      onCheckedChange={(checked) => 
                        setSettings({...settings, pushNotifications: checked})
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Smartphone className="h-4 w-4 text-muted-foreground" />
                      <Label htmlFor="sms-notifications">SMS</Label>
                    </div>
                    <Switch
                      id="sms-notifications"
                      checked={settings.smsNotifications}
                      onCheckedChange={(checked) => 
                        setSettings({...settings, smsNotifications: checked})
                      }
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Tipos de Notificação</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="case-updates">Atualizações de Casos</Label>
                    <Switch
                      id="case-updates"
                      checked={settings.caseUpdates}
                      onCheckedChange={(checked) => 
                        setSettings({...settings, caseUpdates: checked})
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="deadline-alerts">Alertas de Prazo</Label>
                    <Switch
                      id="deadline-alerts"
                      checked={settings.deadlineAlerts}
                      onCheckedChange={(checked) => 
                        setSettings({...settings, deadlineAlerts: checked})
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="client-messages">Mensagens de Clientes</Label>
                    <Switch
                      id="client-messages"
                      checked={settings.clientMessages}
                      onCheckedChange={(checked) => 
                        setSettings({...settings, clientMessages: checked})
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="inactive-alerts">Alertas de Processos Inativos</Label>
                    <Switch
                      id="inactive-alerts"
                      checked={settings.showInactiveAlerts}
                      onCheckedChange={(checked) => 
                        setSettings({...settings, showInactiveAlerts: checked})
                      }
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Configurações de Notificações Push */}
          <PushNotificationSettings />
        </TabsContent>

        {/* Aparência */}
        <TabsContent value="appearance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Personalização da Interface</CardTitle>
              <CardDescription>
                Customize a aparência do sistema conforme sua preferência
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Tema</Label>
                  <Select
                    value={settings.theme}
                    onValueChange={(value) => setSettings({...settings, theme: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">
                        <div className="flex items-center gap-2">
                          <Sun className="h-4 w-4" />
                          Claro
                        </div>
                      </SelectItem>
                      <SelectItem value="dark">
                        <div className="flex items-center gap-2">
                          <Moon className="h-4 w-4" />
                          Escuro
                        </div>
                      </SelectItem>
                      <SelectItem value="system">
                        <div className="flex items-center gap-2">
                          <Monitor className="h-4 w-4" />
                          Sistema
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Tamanho da Fonte</Label>
                  <Select
                    value={settings.fontSize}
                    onValueChange={(value) => setSettings({...settings, fontSize: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Pequena</SelectItem>
                      <SelectItem value="medium">Média</SelectItem>
                      <SelectItem value="large">Grande</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="compact-mode">Modo Compacto</Label>
                  <Switch
                    id="compact-mode"
                    checked={settings.compactMode}
                    onCheckedChange={(checked) => 
                      setSettings({...settings, compactMode: checked})
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Segurança */}
        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Configurações de Segurança</CardTitle>
              <CardDescription>
                Proteja sua conta com configurações avançadas de segurança
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="2fa">Autenticação de Dois Fatores</Label>
                    <p className="text-sm text-muted-foreground">
                      Adicione uma camada extra de segurança
                    </p>
                  </div>
                  <Switch
                    id="2fa"
                    checked={settings.twoFactorAuth}
                    onCheckedChange={(checked) => 
                      setSettings({...settings, twoFactorAuth: checked})
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Timeout da Sessão</Label>
                  <Select
                    value={settings.sessionTimeout}
                    onValueChange={(value) => setSettings({...settings, sessionTimeout: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15min">15 minutos</SelectItem>
                      <SelectItem value="30min">30 minutos</SelectItem>
                      <SelectItem value="1hour">1 hora</SelectItem>
                      <SelectItem value="4hours">4 horas</SelectItem>
                      <SelectItem value="never">Nunca</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="login-alerts">Alertas de Login</Label>
                  <Switch
                    id="login-alerts"
                    checked={settings.loginAlerts}
                    onCheckedChange={(checked) => 
                      setSettings({...settings, loginAlerts: checked})
                    }
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <Button variant="outline" className="w-full">
                  <Key className="h-4 w-4 mr-2" />
                  Alterar Senha
                </Button>
                <Button variant="outline" className="w-full">
                  <Download className="h-4 w-4 mr-2" />
                  Baixar Chaves de Recuperação
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Privacidade */}
        <TabsContent value="privacy" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Configurações de Privacidade</CardTitle>
              <CardDescription>
                Controle como seus dados são utilizados
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="data-sharing">Compartilhamento de Dados</Label>
                    <p className="text-sm text-muted-foreground">
                      Permitir compartilhamento para melhorar o serviço
                    </p>
                  </div>
                  <Switch
                    id="data-sharing"
                    checked={settings.dataSharing}
                    onCheckedChange={(checked) => 
                      setSettings({...settings, dataSharing: checked})
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="analytics">Rastreamento de Analytics</Label>
                    <p className="text-sm text-muted-foreground">
                      Ajuda a melhorar a experiência do usuário
                    </p>
                  </div>
                  <Switch
                    id="analytics"
                    checked={settings.analyticsTracking}
                    onCheckedChange={(checked) => 
                      setSettings({...settings, analyticsTracking: checked})
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="marketing">E-mails de Marketing</Label>
                    <p className="text-sm text-muted-foreground">
                      Receber novidades e promoções
                    </p>
                  </div>
                  <Switch
                    id="marketing"
                    checked={settings.marketingEmails}
                    onCheckedChange={(checked) => 
                      setSettings({...settings, marketingEmails: checked})
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dados */}
        <TabsContent value="data" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Gerenciamento de Dados</CardTitle>
              <CardDescription>
                Exporte ou exclua seus dados do sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Exportar Dados</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Baixe uma cópia de todos os seus dados em formato JSON
                  </p>
                  <Button onClick={handleExportData} variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Exportar Dados
                  </Button>
                </div>

                <div className="p-4 border border-destructive/20 rounded-lg">
                  <h4 className="font-medium mb-2 text-destructive">Zona de Perigo</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Esta ação não pode ser desfeita. Seus dados serão permanentemente removidos.
                  </p>
                  <Button onClick={handleDeleteAccount} variant="destructive">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir Conta
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Administração de Usuários (apenas CEO) */}
        {profile?.role === "CEO" && (
          <TabsContent value="admin-users" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Aprovação de Usuários</CardTitle>
                <CardDescription>
                  Libere ou rejeite contas pendentes de acesso
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <UserApprovals />
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Botão de Salvar */}
      <div className="flex justify-end">
        <Button onClick={handleSave} className="w-full sm:w-auto">
          <Save className="h-4 w-4 mr-2" />
          Salvar Configurações
        </Button>
      </div>
    </div>
  );
};

export default Settings;